// T362 — show WHERE a search matched, without ever building markup.
//
// This is the first highlighting in the app, so it sets the precedent, and the
// precedent has to be the safe one. `dangerouslySetInnerHTML` appears zero
// times in this frontend today and must keep appearing zero times here in
// particular: the text being highlighted is a mail body, written by an external
// sender. MailThread already says so out loud — "nothing stored is markup" —
// and the way to keep that true is to split into React nodes and let React
// escape every piece.
//
// The needle is matched as a literal, the same rule the server search follows,
// because `C++` and `(m/f/d)` are ordinary in this corpus.

import { escapeRegex } from '@hunter/shared/escapeRegex';

/**
 * Split `text` on every case-insensitive occurrence of `needle`.
 *
 * @returns an array of strings and <mark> elements, safe to render directly.
 */
export function highlight(text, needle) {
  const source = String(text ?? '');
  const term = String(needle ?? '').trim();
  if (!term) return [source];

  const re = new RegExp(escapeRegex(term), 'gi');
  const out = [];
  let last = 0;
  let m;
  // `exec` in a loop rather than `split`, so the matched text is preserved
  // exactly as the sender wrote it — casing included.
  while ((m = re.exec(source)) !== null) {
    if (m.index > last) out.push(source.slice(last, m.index));
    out.push(
      <mark key={`${m.index}-${m[0]}`} className="rounded-sm bg-brand-600/30 text-brand-300">
        {m[0]}
      </mark>,
    );
    last = m.index + m[0].length;
    // A zero-length match cannot happen with an escaped non-empty needle, but
    // an infinite loop is a bad way to find out.
    if (m[0].length === 0) break;
  }
  if (last < source.length) out.push(source.slice(last));
  return out;
}

/**
 * The slice of a long body around the first match, so a result row shows the
 * sentence the word is in rather than the first line of a signature block.
 */
export function snippetAround(text, needle, radius = 90) {
  const source = String(text ?? '').replace(/\s+/g, ' ').trim();
  const term = String(needle ?? '').trim();
  if (!term) return source.slice(0, radius * 2);

  const at = source.toLowerCase().indexOf(term.toLowerCase());
  if (at < 0) return source.slice(0, radius * 2);

  const start = Math.max(0, at - radius);
  const end = Math.min(source.length, at + term.length + radius);
  return (start > 0 ? '…' : '') + source.slice(start, end) + (end < source.length ? '…' : '');
}
